import React, { useMemo, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  Bug,
  LogOut,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Repeat2,
  Sun,
  UserRound,
} from 'lucide-react';

import { useAuth } from '../../contexts/AuthContext';
import { useRecadastramentoResumo } from '../../hooks/useRecadastramentoResumo';
import { useTheme } from '../../contexts/ThemeContext';
import useAuditLog from '../../hooks/useAuditLog';
import { buildServerNavigation, groupServerNavigation } from '../../config/serverNavigation';

const SIDEBAR_COLLAPSED_STORAGE_KEY = 'sisdp_server_sidebar_collapsed';

const readInitialCollapsedState = () => {
  try {
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === 'true';
  } catch (error) {
    return false;
  }
};

const Sidebar = () => {
  const {
    user,
    logout,
    vinculos,
    vinculoSelecionado,
    limparVinculoSelecionado,
  } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { logAction } = useAuditLog();
  const navigate = useNavigate();
  const [imgError, setImgError] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(readInitialCollapsedState);

  const {
    recadastramentoData,
    isVinculoElegivel: isRecadastramentoElegivel,
  } = useRecadastramentoResumo({ user, vinculo: vinculoSelecionado });
  const recadastramentoStatus = recadastramentoData?.status || null;

  const navigation = useMemo(() => buildServerNavigation({
    permissions: user?.serverPortalPermissions || {},
    vinculoSelecionado,
    recadastramentoElegivel: isRecadastramentoElegivel,
    recadastramentoStatus,
  }), [user?.serverPortalPermissions, vinculoSelecionado, isRecadastramentoElegivel, recadastramentoStatus]);

  const groups = useMemo(() => groupServerNavigation(navigation), [navigation]);
  const logoUrl = user?.logoUrl || 'https://firebasestorage.googleapis.com/v0/b/portal-sisfol.firebasestorage.app/o/Logos_municipais%2Fdefault.png?alt=media';

  const handleToggleSidebar = () => {
    setIsCollapsed((current) => {
      const next = !current;
      try {
        window.localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(next));
      } catch (error) {
        // Preferência visual não deve bloquear a navegação quando o storage estiver indisponível.
      }
      return next;
    });
  };

  const handleOpenTechnicalSupport = () => {
    window.dispatchEvent(new CustomEvent('sisfol:open-support', {
      detail: { route: window.location.pathname, message: 'Quero informar um problema no sistema.' },
    }));
  };

  const handleLogout = async () => {
    await logAction('LOGOUT', {
      entityType: 'AUTHENTICATION',
      entityId: user?.uid || 'N/A',
      modulo: 'Portal do Servidor',
      pagina: 'Menu lateral',
      tela: 'Menu lateral',
      tipoAcao: 'autenticacao',
      alterouDados: false,
      descricao: 'Servidor encerrou a sessão pelo menu lateral.',
    });

    logout();
    navigate('/login');
  };

  return (
    <aside
      className={`hidden shrink-0 flex-col border-r border-[var(--sisdp-color-border)] bg-[var(--sisdp-color-surface)] transition-[width] duration-200 lg:flex ${
        isCollapsed ? 'w-[var(--server-sidebar-collapsed-width)]' : 'w-[var(--server-sidebar-width)]'
      }`}
      data-collapsed={isCollapsed ? 'true' : 'false'}
      aria-label="Menu lateral do Portal do Servidor"
    >
      <div className={`border-b border-[var(--sisdp-color-border)] ${isCollapsed ? 'px-2 pb-3 pt-4' : 'px-4 pb-5 pt-5'}`}>
        <div className={`flex ${isCollapsed ? 'flex-col items-center gap-2' : 'items-start gap-3'}`}>
          <div className={`flex shrink-0 items-center justify-center overflow-hidden border border-[var(--sisdp-color-border)] bg-white shadow-sm dark:bg-gray-900 ${isCollapsed ? 'h-11 w-11 rounded-xl' : 'h-12 w-12 rounded-2xl'}`}>
            {logoUrl && !imgError ? (
              <img
                src={logoUrl}
                alt="Logo do município"
                className="h-full w-full object-contain p-1.5"
                onError={() => setImgError(true)}
              />
            ) : (
              <UserRound className="text-blue-600 dark:text-blue-300" size={24} />
            )}
          </div>

          {!isCollapsed && (
            <div className="min-w-0 flex-1 pt-0.5">
              <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-blue-600 dark:text-blue-300">Portal do Servidor</p>
              <p className="mt-1 truncate text-sm font-black text-[var(--sisdp-color-text)]">{user?.name || user?.nome || 'Servidor'}</p>
              {vinculoSelecionado?.matricula && (
                <p className="mt-0.5 text-xs text-[var(--sisdp-color-text-muted)]">Matrícula {vinculoSelecionado.matricula}</p>
              )}
            </div>
          )}

          <button
            type="button"
            onClick={handleToggleSidebar}
            className={`server-touch-target inline-flex shrink-0 items-center justify-center rounded-xl border border-[var(--sisdp-color-border)] text-[var(--sisdp-color-text-muted)] transition-colors hover:bg-[var(--sisdp-color-surface-subtle)] hover:text-[var(--sisdp-color-text)] ${isCollapsed ? '' : 'ml-auto'}`}
            aria-label={isCollapsed ? 'Expandir menu lateral' : 'Recolher menu lateral'}
            aria-expanded={!isCollapsed}
            title={isCollapsed ? 'Expandir menu lateral' : 'Recolher menu lateral'}
          >
            {isCollapsed ? <PanelLeftOpen size={19} aria-hidden="true" /> : <PanelLeftClose size={19} aria-hidden="true" />}
          </button>
        </div>

        {Array.isArray(vinculos) && vinculos.length > 1 && (
          <button
            type="button"
            onClick={limparVinculoSelecionado}
            className={`mt-3 flex min-h-11 items-center rounded-xl border border-[var(--sisdp-color-border)] bg-[var(--sisdp-color-surface-subtle)] text-sm font-bold text-[var(--sisdp-color-text)] transition-colors hover:bg-[var(--sisdp-color-surface-muted)] ${isCollapsed ? 'w-11 justify-center px-0' : 'w-full justify-center gap-2 px-3'}`}
            aria-label={isCollapsed ? 'Trocar vínculo' : undefined}
            title={isCollapsed ? 'Trocar vínculo' : undefined}
          >
            <Repeat2 size={17} aria-hidden="true" />
            {!isCollapsed && 'Trocar vínculo'}
          </button>
        )}
      </div>

      <nav className={`sisfol-soft-scrollbar flex-1 overflow-y-auto py-4 ${isCollapsed ? 'px-2' : 'px-3'}`} aria-label="Navegação do Portal do Servidor">
        {groups.map((group) => (
          <div key={group.category} className={`${isCollapsed ? 'mb-2' : 'mb-5'} last:mb-1`}>
            {!isCollapsed && group.category !== 'Principal' && (
              <h2 className="mb-2 px-3 text-[10px] font-extrabold uppercase tracking-[0.16em] text-gray-400 dark:text-gray-500">{group.category}</h2>
            )}
            <div className="space-y-1">
              {group.items.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.id}
                    to={item.path}
                    end={item.path === '/dashboard'}
                    state={item.state}
                    aria-label={isCollapsed ? item.label : undefined}
                    title={isCollapsed ? item.label : undefined}
                    className={({ isActive }) => `flex min-h-11 items-center rounded-xl py-2 text-sm font-bold transition-colors ${isCollapsed ? 'justify-center px-0' : 'gap-3 px-3'} ${isActive
                      ? 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-200'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-950 dark:text-gray-300 dark:hover:bg-gray-800/70 dark:hover:text-white'}`}
                  >
                    <Icon size={19} className="shrink-0" strokeWidth={2.1} aria-hidden="true" />
                    {!isCollapsed && <span className="min-w-0 flex-1 truncate">{item.label}</span>}
                  </NavLink>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      <div className={`border-t border-[var(--sisdp-color-border)] ${isCollapsed ? 'p-2' : 'p-3'}`}>
        <button
          type="button"
          onClick={handleOpenTechnicalSupport}
          className={`mb-2 flex min-h-11 items-center rounded-xl border border-violet-200 bg-violet-50 text-xs font-bold text-violet-700 hover:bg-violet-100 dark:border-violet-900/60 dark:bg-violet-950/30 dark:text-violet-200 ${isCollapsed ? 'w-11 justify-center px-0' : 'w-full justify-center gap-2 px-3'}`}
          aria-label={isCollapsed ? 'Problema no sistema' : undefined}
          title={isCollapsed ? 'Problema no sistema' : undefined}
        >
          <Bug size={16} aria-hidden="true" />
          {!isCollapsed && 'Problema no sistema'}
        </button>
        <div className={isCollapsed ? 'grid grid-cols-1 gap-2' : 'grid grid-cols-2 gap-2'}>
          <button
            type="button"
            onClick={toggleTheme}
            className={`flex min-h-11 items-center justify-center rounded-xl border border-[var(--sisdp-color-border)] bg-[var(--sisdp-color-surface)] text-xs font-bold text-[var(--sisdp-color-text)] hover:bg-[var(--sisdp-color-surface-subtle)] ${isCollapsed ? 'w-11 px-0' : 'gap-2 px-2'}`}
            aria-label={isCollapsed ? (theme === 'light' ? 'Ativar tema escuro' : 'Ativar tema claro') : undefined}
            title={isCollapsed ? (theme === 'light' ? 'Tema escuro' : 'Tema claro') : undefined}
          >
            {theme === 'light' ? <Moon size={16} /> : <Sun size={16} />}
            {!isCollapsed && (theme === 'light' ? 'Escuro' : 'Claro')}
          </button>
          <button
            type="button"
            onClick={handleLogout}
            className={`flex min-h-11 items-center justify-center rounded-xl border border-red-200 bg-red-50 text-xs font-bold text-red-700 hover:bg-red-100 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-200 dark:hover:bg-red-950/50 ${isCollapsed ? 'w-11 px-0' : 'gap-2 px-2'}`}
            aria-label={isCollapsed ? 'Sair' : undefined}
            title={isCollapsed ? 'Sair' : undefined}
          >
            <LogOut size={16} />
            {!isCollapsed && 'Sair'}
          </button>
        </div>
        {!isCollapsed && <p className="mt-3 text-center text-[10px] text-gray-400 dark:text-gray-600">SISDP • {new Date().getFullYear()}</p>}
      </div>
    </aside>
  );
};

export default Sidebar;
